import { Avatar, Box, Grid, Pagination, Typography } from "@mui/material";
import { APP_PAGE_SIZE_DEFAULT } from "app/appConstants";
import { AppImages } from "app/appImages";
import { MprcRequestErrorDialog, MprcRequestIndicator, MprcTextMuted } from "components";
import { ButtonBack } from "components/buttons";
import { AnswersAlternativeDtoPaginatedList, AssessmentInstanceDto, AssessmentQuestionDto } from "generated/CoreApi_v1";
import { Formaters } from "lib/Formaters";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useInstanceContext } from "views/assessment/player/AssPlayerBase";

/////////////////////////////////////////////////////////////////////////////////

interface IProps {
    instance: AssessmentInstanceDto;
    question: AssessmentQuestionDto;
    deployId?: string;
}

export const AssPlayerAlternativeAnswers: React.FC<IProps> = (props) => {

    const ctx = useInstanceContext();

    const { instance, deployId, question } = props;

    const api = useCoreApi();
    const { t } = useTranslation("assessment");

    const [loading, setLoading] = useState<boolean>(true);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [viewError, setViewError] = useState<any>();
    const [viewData, setViewData] = useState<AnswersAlternativeDtoPaginatedList | null>();

    ////////////////////////////////////////////////////////////////////////////////
    /// EFFECT's
    ////////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        async function getData() {
            try {
                const result = await api.v1.assPlayer.getUserAnswersAlternative({
                    instanceId: instance.instanceId,
                    questionId: question.questionId,
                    version: question.version,
                    deployId: deployId ?? null,
                    alternativeId: ctx.selectedAlternative!.alternativeId,
                    onlyTrueAnswer: false,
                    searchText: null,
                    pageNumber,
                    pageSize: APP_PAGE_SIZE_DEFAULT
                });
                setViewData(result.data);
            } catch (e) {
                setViewData(null);
                setViewError(e);
            } finally {
                setLoading(false);
            }
        }

        if (loading) getData();
    }, [loading]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <>
            <MprcRequestIndicator visible={loading} />

            <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(null)} />

            <Box
                sx={
                    (theme) =>
                    ({
                        backgroundColor: theme.palette.paper.dark,
                        borderBottomColor: theme.extended.workArea.navigationBar.separatorColor,
                        padding: theme.spacing(1),
                        borderRadius: "8px"
                    })
                }>
                <Grid container justifyContent="space-between" alignItems="center">
                    <Grid>
                        <ButtonBack
                            key="btnBack"
                            rounded
                            variant="text"
                            onClick={() => ctx.changeSelectedAlternative(null)}
                        />
                    </Grid>
                    <Grid>
                        <Typography
                            variant="h5"
                            gutterBottom
                            component="div"
                            color="muted"
                            sx={
                                (theme) => ({
                                    marginBottom: 0
                                })}
                        >
                            {ctx.selectedAlternative?.text}
                        </Typography>
                    </Grid>
                </Grid>
            </Box>

            {
                viewData && viewData.totalRows > 0 && (
                    <Box marginTop={1}>
                        <Grid
                            container
                            flexWrap="wrap"
                            alignContent="space-between"
                            spacing={1}
                        >
                            {
                                viewData.items.map((item, index) => {
                                    const numericAnswer = item.numericAnswer ?? 0;
                                    return (
                                        <Grid size={{ xs: 6 }} key={index}>
                                            <Grid
                                                container
                                                alignItems="center"
                                                spacing={1}
                                                wrap="nowrap"
                                            >
                                                <Grid>
                                                    <Avatar
                                                        sx={{ width: 48, height: 48 }}
                                                        src={item.thumbnailUrl ?? AppImages.UserThumbnail}
                                                    />
                                                </Grid>
                                                <Grid>
                                                    <MprcTextMuted>
                                                        {Formaters.truncateString(item.name, 100)}: {Formaters.number(numericAnswer, (numericAnswer % 1) == 0 ? 0 : 2)} {item.totalAnswer > 1 ? `(${t("AssPlayer.UserDrawer.TimesText", { count: item.totalAnswer })})` : ""}
                                                    </MprcTextMuted>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    );
                                })
                            }
                        </Grid>
                        {
                            !loading && viewData && viewData.totalPages > 1 && (
                                <Box display="flex" mt={2}>
                                    <Pagination
                                        shape="rounded"
                                        color="primary"
                                        page={viewData.pageNumber}
                                        count={viewData.totalPages}
                                        sx={{ margin: "auto" }}
                                        onChange={(e, page) => {
                                            if (page != pageNumber) {
                                                setPageNumber(page);
                                                setLoading(true);
                                            }
                                        }}
                                    />
                                </Box>
                            )
                        }
                    </Box>
                )
            }
        </>
    );
};
